import { useEffect, useState } from "react";
import { useBlocker, useNavigate, useOutletContext } from "react-router-dom";
import {
  Alert,
  Button,
  SelectInput,
  Tabs,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import {
  type FeeManagerConfig,
  type FeeType,
  getConfigDefaults
} from "@dealer-center/ddc-pages-fee-manager-interface";
import {
  useFees,
  useGetConfig,
  useSaveConfig,
  useSaveFees,
  useVersion
} from "@dealer-center/ddc-pages-fee-manager-fetch";
import { useFeeState } from "./hooks/useFeeState";
import { FeeRow } from "./components/FeeRow";
import { FeeRowSkeleton } from "./components/FeeRowSkeleton";
import { TotalFeesDisplay } from "./components/TotalFeesDisplay";
import { AddFeeModal } from "./components/AddFeeModal";
import { ConfigurationModal } from "./components/ConfigurationModal";
import { UnsavedChangesModal } from "./components/UnsavedChangesModal";
import {
  ButtonGroup,
  HeaderCell,
  InstructionText,
  PageContainer,
  PageTitle,
  TableActionsRow,
  TableBody,
  TableContainer,
  TableHeader,
  TabsContainer
} from "./styles/FeeManager.styles";
import LABELS from "./labels.json";

export const FeeManager = () => {
  const navigate = useNavigate();
  const { toastError } = useToaster();
  const { userEntity, ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();
  const { "fee-manager-access": isFeeManagerEnabled } = useCC9Flags();

  const accountId = ccUserInfo?.accountId ?? "";
  const isInvalidTenant = accountId.toLowerCase() === "ddc";
  const [activeTab, setActiveTab] = useState<FeeType>("NEW");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [editingFeeId, setEditingFeeId] = useState<string | null>(null);

  // Initialize locale labels once on mount
  useEffect(() => {
    Locale.setLabelsOnce(LABELS);
  }, []);

  // Fetch data
  const { data: versionData } = useVersion();

  useEffect(() => {
    if (versionData) {
      // eslint-disable-next-line no-console
      console.log("[FeeManager] version:", versionData.version);
    }
  }, [versionData]);

  const {
    data: feesData,
    isError: isFeesError,
    isLoading: isFeesLoading
  } = useFees({ accountId, disabled: isInvalidTenant });
  const { data: configData, isError: isConfigError } = useGetConfig({
    accountId,
    disabled: isInvalidTenant
  });
  const feeState = useFeeState();

  // Sync server fees into draft state when loaded
  useEffect(() => {
    if (feesData?.fees) {
      feeState.setServerAndDraftFees(feesData.fees);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feesData]);

  useEffect(() => {
    if (isFeesError) {
      toastError(String(Locale.getText("FEEMANAGER_ERROR_LOAD_FEES")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeesError]);

  useEffect(() => {
    if (isConfigError) {
      toastError(String(Locale.getText("FEEMANAGER_ERROR_LOAD_CONFIG")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigError]);

  const [configDraft, setConfigDraft] = useState<FeeManagerConfig>(
    getConfigDefaults("DDC")
  );

  // Sync server config into draft when loaded
  useEffect(() => {
    if (configData) {
      setConfigDraft(configData);
    }
  }, [configData]);

  // Redirect if feature flag disabled and not internal user
  useEffect(() => {
    if (isFeeManagerEnabled === false && !userEntity?.internal) {
      navigate("/");
    }
  }, [isFeeManagerEnabled, navigate, userEntity]);

  // Block in-app navigation when there are unsaved changes
  const blocker = useBlocker(feeState.isDirty);

  // Block browser-level navigation (refresh / close tab) when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (feeState.isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [feeState.isDirty]);

  // Mutations
  const saveFeesMutation = useSaveFees({ accountId });
  const saveConfigMutation = useSaveConfig({ accountId });

  // Derived state
  const isSaving = saveFeesMutation.isPending;
  const isFeeEditingDisabled = isSaving;

  const visibleFees = (
    feeState.showRemoved
      ? feeState.draftFees.filter(f => f.removed)
      : feeState.draftFees.filter(f => !f.removed)
  ).filter(f =>
    activeTab === "NEW"
      ? f.type === "NEW" || f.type === "ALL"
      : f.type === "USED" || f.type === "ALL"
  );

  const editingFee =
    editingFeeId !== null
      ? (feeState.draftFees.find(f => f.id === editingFeeId) ?? null)
      : null;

  const tabs = [
    {
      active: activeTab === "NEW",
      disabled: isSaving,
      label: String(Locale.getText("FEEMANAGER_NEW_TAB")),
      value: 0
    },
    {
      active: activeTab === "USED",
      disabled: isSaving,
      label: String(Locale.getText("FEEMANAGER_PREOWNED_TAB")),
      value: 1
    }
  ];

  // Handlers
  const handleSave = () => {
    saveFeesMutation.mutate(
      { fees: feeState.draftFees, userId: ccUserInfo?.userId ?? "" },
      {
        onSuccess: data => {
          if (data.success) {
            feeState.setServerAndDraftFees(data.fees);
          } else if (data.error === "CONFLICT") {
            toastError(String(Locale.getText("FEEMANAGER_CONFLICT_ERROR")));
          }
        },
        onError: () => {
          toastError(String(Locale.getText("FEEMANAGER_ERROR_SAVE_TITLE")));
        }
      }
    );
  };

  const handleTabActivate = (index: number) => {
    const newTab: FeeType = index === 0 ? "NEW" : "USED";
    setActiveTab(newTab);
    setEditingFeeId(null);
  };

  const handleAdd = (data: {
    name: string;
    amount: number | null;
    type: FeeType;
  }) => {
    feeState.addDraftFee({
      amount: data.amount,
      id: crypto.randomUUID(),
      name: data.name,
      removed: false,
      type: data.type
    });
    setIsAddModalOpen(false);
  };

  const handleOpenEdit = (feeId: string) => {
    setEditingFeeId(feeId);
  };

  const handleCloseEdit = () => {
    setEditingFeeId(null);
  };

  const handleUpdate = (
    updates: Partial<{ name: string; amount: number | null; type: FeeType }>
  ) => {
    if (editingFeeId) {
      feeState.updateDraftFee(editingFeeId, updates);
    }
  };

  const handleDeleteFee = (feeId: string) => {
    feeState.softDelete([feeId]);
  };

  const handleRestoreFee = (feeId: string) => {
    feeState.restore([feeId]);
  };

  const handleOpenConfig = () => {
    setIsConfigOpen(true);
  };

  const handleCloseConfig = () => {
    setIsConfigOpen(false);
    setConfigDraft(configData ?? getConfigDefaults("DDC"));
  };

  const handleConfigChange = (patch: Partial<FeeManagerConfig>) => {
    setConfigDraft(prev => ({ ...prev, ...patch }));
  };

  const handleSaveConfig = () => {
    saveConfigMutation.mutate(
      { config: configDraft, userId: ccUserInfo?.userId ?? "" },
      {
        onSuccess: data => {
          if (data.success) {
            setConfigDraft(data.config);
            setIsConfigOpen(false);
          } else if (data.error === "CONFLICT") {
            toastError(String(Locale.getText("FEEMANAGER_CONFLICT_ERROR")));
          }
        },
        onError: () => {
          toastError(
            String(Locale.getText("FEEMANAGER_ERROR_SAVE_CONFIG_TITLE"))
          );
        }
      }
    );
  };

  return (
    <PageContainer>
      <PageTitle>
        <Typography variant={"h1"}>
          {String(Locale.getText("FEEMANAGER_TITLE"))}
        </Typography>
      </PageTitle>

      {isInvalidTenant && (
        <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
          {String(Locale.getText("FEEMANAGER_ERROR_INVALID_TENANT"))}
        </Alert>
      )}

      {/* Save / Cancel bar */}
      <SaveAndCancel
        analyticsEventName="Fee Manager"
        cancelButtonText={String(Locale.getText("FEEMANAGER_CANCEL"))}
        isDisabled={{
          cancel: !feeState.isDirty,
          save: !feeState.isDirty
        }}
        isSaving={isSaving}
        saveButtonText={String(Locale.getText("FEEMANAGER_SAVE"))}
        savingText={String(Locale.getText("FEEMANAGER_SAVING"))}
        onCancel={feeState.handleCancel}
        onSave={handleSave}
      >
        <Button
          analyticsConfiguration={{
            eventName: "Fee Manager Configuration",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          disabled={configData === undefined}
          sx={{ marginRight: "auto" }}
          onClick={handleOpenConfig}
        >
          {String(Locale.getText("CONFIGURATION"))}
        </Button>
      </SaveAndCancel>

      {/* Action Buttons */}
      <TableActionsRow>
        <SelectInput
          data-testid="fee-view-filter"
          displayDeselectOption={false}
          id="fee-view-filter"
          name="fee-view-filter"
          options={[
            {
              value: "active",
              label: String(Locale.getText("FEEMANAGER_SHOW_ACTIVE"))
            },
            {
              value: "deleted",
              label: String(Locale.getText("FEEMANAGER_SHOW_DELETED"))
            }
          ]}
          value={feeState.showRemoved ? "deleted" : "active"}
          onChange={e => {
            const shouldShowRemoved = e.target.value === "deleted";
            if (shouldShowRemoved !== feeState.showRemoved) {
              feeState.toggleShowRemoved();
            }
          }}
        />
        <ButtonGroup>
          <Button
            analyticsConfiguration={{
              eventName: "Fee Manager Add Fee",
              eventProperties: undefined
            }}
            buttonStyle="primary"
            disabled={
              isFeeEditingDisabled ||
              configData === undefined ||
              feesData === undefined
            }
            onClick={() => setIsAddModalOpen(true)}
          >
            {String(Locale.getText("FEEMANAGER_ADDFEE"))}
          </Button>
        </ButtonGroup>
      </TableActionsRow>

      <TabsContainer disabled={isSaving}>
        <Tabs id="fee-tabs" tabs={tabs} onActivate={handleTabActivate} />
      </TabsContainer>

      {/* Table */}
      <TableContainer>
        <InstructionText>
          {String(Locale.getText("FEEMANAGER_CLICK_ENTRY_TO_EDIT"))}
        </InstructionText>

        <TableHeader disabled={isSaving}>
          <HeaderCell>{String(Locale.getText("FEEMANAGER_NAME"))}</HeaderCell>
          <HeaderCell $center>
            {String(Locale.getText("FEEMANAGER_TYPE_HEADER"))}
          </HeaderCell>
          <HeaderCell $shiftLeft>
            {String(Locale.getText("FEEMANAGER_AMOUNT"))}
          </HeaderCell>
          <HeaderCell />
        </TableHeader>

        {isFeesLoading ? (
          <TableBody disabled>
            <FeeRowSkeleton count={5} />
          </TableBody>
        ) : (
          <TableBody>
            {visibleFees.map(fee => (
              <FeeRow
                disabled={isFeeEditingDisabled}
                fee={fee}
                key={fee.id}
                locale={Locale.getLocale()}
                onDelete={handleDeleteFee}
                onOpenEdit={handleOpenEdit}
                onRestore={handleRestoreFee}
              />
            ))}
          </TableBody>
        )}

        {!feeState.showRemoved && (
          <>
            <TotalFeesDisplay
              disabled={false}
              locale={Locale.getLocale()}
              total={feeState.totalAmountNew}
              vehicleType="NEW"
            />
            <TotalFeesDisplay
              disabled={false}
              locale={Locale.getLocale()}
              total={feeState.totalAmountUsed}
              vehicleType="USED"
            />
          </>
        )}
      </TableContainer>

      {/* Add Fee Modal */}
      <AddFeeModal
        defaultType={activeTab}
        existingFees={feeState.draftFees}
        isOpen={isAddModalOpen}
        mode="add"
        onAdd={handleAdd}
        onCancel={() => setIsAddModalOpen(false)}
      />

      {/* Edit Fee Modal */}
      {editingFee !== null && (
        <AddFeeModal
          isOpen
          existingFees={feeState.draftFees}
          fee={editingFee}
          mode="edit"
          onClose={handleCloseEdit}
          onUpdate={handleUpdate}
        />
      )}

      {/* Configuration Modal */}
      <ConfigurationModal
        config={configDraft}
        isConfigDirty={
          JSON.stringify(configDraft) !==
          JSON.stringify(configData ?? getConfigDefaults("DDC"))
        }
        isOpen={isConfigOpen}
        isSaving={saveConfigMutation.isPending}
        onChange={handleConfigChange}
        onClose={handleCloseConfig}
        onSave={handleSaveConfig}
      />

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={blocker.state === "blocked"}
        onLeave={() => blocker.proceed?.()}
        onStay={() => blocker.reset?.()}
      />
    </PageContainer>
  );
};
